import { Box, MenuItem } from '@mui/material';
import { isAfter } from 'date-fns';
import { Form, Formik } from 'formik';
import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { BoardingGateProperties, ResourceType, useQueryGetGetResources } from '../../../../backend';
import { stopPropagation } from '../../../../utils/dom.utils';
import { RoundButton } from '../../../Button/RoundButton/RoundButton';
import { Radio, RadioGroupField } from '../../../form/RadioGroup/RadioGroup';
import { FormikSelect } from '../../../form/Select/FormikSelect';
import { useRmsGanttState, useRmsGanttValue, useSetRmsGantt } from '../../RmsContent/RmsContext';
import { StyledButtonContainer, StyledContainer, StyledScheduledModeBox } from './ViewModeForm.style';

export interface ViewModeFormValues {
    viewModeScheduled: 'deviations' | 'scheduled';
    resourceFilterCriteria: string | null;
}

export const ViewModeForm = memo(function ViewModeForm({ onClose }: { onClose?: () => void }) {
    const day = useRmsGanttValue('day');
    const resourceType = useRmsGanttValue('resourceType');
    const setResourcesByType = useSetRmsGantt('resourcesByType');
    const [rmsFilters, setRmsFilters] = useRmsGanttState('rmsFilters');
    const viewModeScheduledDisabled = isAfter(day, new Date());

    const { data: allResources } = useQueryGetGetResources({ includeNonActive: true });

    const resourceFilterValues = useMemo(() => {
        const selectedTypeResources = allResources.filter(r => r.type === resourceType);
        if (resourceType === ResourceType.BOARDING_GATE) {
            return Array.from(
                new Set(selectedTypeResources?.map(r => (r.content as BoardingGateProperties)?.departureHall)),
            );
        } else {
            return null;
        }
    }, [allResources, resourceType]);

    const onSubmit = useCallback(
        (values: ViewModeFormValues) => {
            setRmsFilters(values);
            onClose?.();
            if (resourceType === ResourceType.BOARDING_GATE) {
                if (values.resourceFilterCriteria) {
                    setResourcesByType(
                        allResources.filter(
                            r =>
                                r.type === resourceType &&
                                (r.content as BoardingGateProperties)?.departureHall === values.resourceFilterCriteria,
                        ),
                    );
                } else {
                    setResourcesByType(allResources.filter(r => r.type === resourceType));
                }
            }
        },
        [allResources, onClose, resourceType, setResourcesByType, setRmsFilters],
    );

    return (
        <Formik initialValues={rmsFilters} onSubmit={onSubmit}>
            {props => (
                <Form>
                    <StyledContainer onClick={stopPropagation}>
                        <FormikSelect
                            name="resourceFilterCriteria"
                            label={<FormattedMessage id="viewMode.popin.resourceFilter" />}
                            fullWidth
                            FormControlProps={{ sx: { marginTop: 2 } }}
                            disabled={resourceType !== ResourceType.BOARDING_GATE}
                        >
                            <MenuItem value={null}>
                                <em>-</em>
                            </MenuItem>
                            {resourceFilterValues?.map(value => (
                                <MenuItem key={value} value={value}>
                                    <em>{value}</em>
                                </MenuItem>
                            ))}
                        </FormikSelect>

                        <RadioGroupField name={'viewModeScheduled'}>
                            <StyledScheduledModeBox>
                                <Radio value={'deviations'} disabled={viewModeScheduledDisabled}>
                                    <Box fontSize={'15px'}>
                                        <FormattedMessage id="viewMode.popin.viewModeDelay" />
                                    </Box>
                                </Radio>
                                <Radio value={'scheduled'} disabled={viewModeScheduledDisabled}>
                                    <Box fontSize={'15px'}>
                                        <FormattedMessage id="viewMode.popin.viewModeScheduled" />
                                    </Box>
                                </Radio>
                            </StyledScheduledModeBox>
                        </RadioGroupField>

                        <StyledButtonContainer>
                            <Box marginRight="5px">
                                <RoundButton size="medium" variant="outlined" onClick={onClose}>
                                    <FormattedMessage id="viewMode.popin.cancel" />
                                </RoundButton>
                            </Box>
                            <Box marginLeft="5px">
                                <RoundButton size="medium" variant="contained" type={'submit'} disabled={!props.dirty}>
                                    <FormattedMessage id="viewMode.popin.applyFilters" />
                                </RoundButton>
                            </Box>
                        </StyledButtonContainer>
                    </StyledContainer>
                </Form>
            )}
        </Formik>
    );
});
